import { useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignInAlt, faUserPlus, faHandHoldingHeart, faChevronLeft, faTimes, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import { useDonationContextProvider } from 'context/DonationContextProvider';
import { useBranchContext } from 'context/BranchContext';
import { supabase, authHelpers } from '../../lib/supabase';
import { createDonationPaymentIntent, persistBasketSession } from '../../lib/supabaseQueries';
import { DONATION_STEPS } from './data';
import { PasswordInput } from './PasswordInput';

const AUTH_MODE_META = {
  login: { title: 'Login to your account', subtitle: 'Welcome back — sign in to speed up checkout.' },
  signup: { title: 'Create an account', subtitle: 'Save your details for faster donations next time.' },
  reset: { title: 'Reset your password', subtitle: "We'll email you a link to set a new one." }
};

const AUTH_MODES = { CHOICE: 'choice', LOGIN: 'login', SIGNUP: 'signup', RESET: 'reset' };

function AuthChoiceModal() {
  const { branchId } = useBranchContext();
  const {
    isAuthModalOpen,
    setIsAuthModalOpen,
    authModalSource,
    setIsDonorAuthenticated,
    setDonationStep,
    setDonor,
    basket,
    giftAid,
    transactionFeeAmount,
    subtotal,
    totalAmount,
    transactionFee,
    setClientSecret,
    setStripeKey
  } = useDonationContextProvider();

  const [mode, setMode] = useState(AUTH_MODES.CHOICE);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const isHeaderMode = authModalSource === 'header';
  const organizationId = process.env.REACT_APP_ORGANIZATION_ID;

  const resetState = () => {
    setMode(AUTH_MODES.CHOICE);
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setError(null);
    setIsLoading(false);
  };

  const handleClose = () => {
    resetState();
    setIsAuthModalOpen(false);
  };

  const buildDonorFromDbRecord = (donor) => ({
    title: donor.title || '',
    firstName: donor.first_name || '',
    lastName: donor.last_name || '',
    email: donor.primary_email || donor.email?.[0]?.email || '',
    phoneNumber: donor.primary_phone || donor.phone?.[0]?.phoneNumber || '',
    addressLine1: donor.address?.[0]?.addressLine1 || '',
    addressLine2: donor.address?.[0]?.addressLine2 || '',
    city: donor.address?.[0]?.city || '',
    country: donor.address?.[0]?.country || '',
    postcode: donor.address?.[0]?.postcode || '',
    hearAboutUs: donor.hear_about_us || '',
    type: 'personal',
    isPrimary: true
  });

  const initializePaymentAndNavigate = async (donorObject) => {
    const isRecurring = basket.some(item => item.paymentType === 'monthly');
    const orderDetails = basket.map(item => ({
      amount: item.count * item.donationAmount.value,
      billing_condition: item.paymentType,
      donation_type_id: item.donationType.id,
      package_id: item.packageId || null
    }));

    const { data, error } = await createDonationPaymentIntent({
      donorObject,
      amount: parseFloat(totalAmount),
      orderDetails,
      subtotal: parseFloat(subtotal),
      transactionFees: transactionFee ? parseFloat(transactionFeeAmount) : 0,
      giftAidEligible: giftAid,
      giftAidData: null,
      isRecurring
    });

    if (error) throw new Error(error);
    if (!data?.client_secret) throw new Error('Failed to create payment intent');

    setClientSecret(data.client_secret);
    if (data.publishable_key) setStripeKey(data.publishable_key);

    if (data?.donor_id) {
      await persistBasketSession({
        donorId: data.donor_id,
        basket,
        transactionFee: transactionFee ? transactionFeeAmount : 0,
        subtotal,
        totalAmount,
        branchId,
        organisationId: organizationId,
        giftAidEligible: giftAid,
        giftAidData: null,
        orderDetails,
        paymentIntentId: data.payment_intent_id
      });
    }

    handleClose();
    setDonationStep(DONATION_STEPS.PAYMENT);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const loginEmail = email;

    try {
      const { error: authError } = await authHelpers.signIn({ email: loginEmail, password });

      if (authError) throw new Error(authError.message);
      setIsDonorAuthenticated(true);
      setDonor({ email: loginEmail });

      if (isHeaderMode) {
        toast.success('Signed in successfully!');
        handleClose();
        return;
      }

      // Runs only after signIn has resolved. The donor RLS policy matches on
      // the JWT email, so issuing this before the session exists would send it
      // as anon and always come back empty.
      const { data: donors, error: donorError } = await supabase
        .from('donars')
        .select('*')
        .eq('organisation_id', organizationId)
        .ilike('primary_email', loginEmail)
        .limit(1);

      if (donorError) throw new Error('Failed to look up donor record');

      if (donors && donors.length > 0) {
        const existingDonor = donors[0];
        const donorFormData = buildDonorFromDbRecord(existingDonor);
        setDonor(donorFormData);

        const donorObject = {
          first_name: existingDonor.first_name,
          last_name: existingDonor.last_name,
          branch_id: branchId,
          organisation_id: organizationId,
          hear_about_us: existingDonor.hear_about_us || null,
          email: existingDonor.email || [{ email: loginEmail, isPrimary: true }],
          primary_email: existingDonor.primary_email || loginEmail,
          phone: existingDonor.phone || [],
          primary_phone: existingDonor.primary_phone || null,
          address: existingDonor.address || [],
          primary_address: existingDonor.primary_address || null,
          type: 'individual',
          title: existingDonor.title || 'Mr'
        };

        await initializePaymentAndNavigate(donorObject);
      } else {
        setDonor({ email: loginEmail });
        handleClose();
        setDonationStep(DONATION_STEPS.DONOR_INFORMATION);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setIsLoading(false);
      return;
    }

    const signupEmail = email;

    try {
      const { data: signUpData, error: signUpError } = await authHelpers.signUp({
        email: signupEmail,
        password,
        firstName: '',
        lastName: ''
      });

      if (signUpError) {
        // Supabase returns this when email is already registered (confirm email disabled)
        if (signUpError.message?.toLowerCase().includes('already registered')) {
          setError('An account with this email already exists. Please login instead.');
          setIsLoading(false);
          return;
        }
        throw new Error(signUpError.message);
      }



      // Supabase returns identities=[] for duplicate email when confirm email is enabled
      if (!signUpData?.user?.identities?.length) {
        setError('An account with this email already exists. Please login instead.');
        setIsLoading(false);
        return;
      }

      // With email confirmation enabled Supabase returns a user but no session:
      // the account exists, but it isn't signed in yet. Only claim authentication
      // when a session actually came back, so the header and checkout don't show
      // a logged-in state the Supabase session doesn't back.
      if (signUpData?.session) {
        setIsDonorAuthenticated(true);
        toast.success('Account created!');
      } else {
        toast.success('Account created — check your email to confirm it before signing in.');
      }
      setDonor({ email: signupEmail });

      if (isHeaderMode) {
        handleClose();
        return;
      }

      handleClose();
      setDonationStep(DONATION_STEPS.DONOR_INFORMATION);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGuest = () => {
    handleClose();
    setDonationStep(DONATION_STEPS.DONOR_INFORMATION);
  };

  return (
    <>
      <style>{`
        .auth-modal-backdrop {
          backdrop-filter: blur(8px) !important;
          -webkit-backdrop-filter: blur(8px) !important;
          background-color: rgba(0, 0, 0, 0.4) !important;
        }
        .auth-modal-header {
          background-color: #012f86;
          padding: 26px 28px 22px;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
        }
        .auth-modal-header-text {
          min-width: 0;
        }
        .auth-modal-header-text::after {
          content: '';
          display: block;
          width: 40px;
          height: 3px;
          margin-top: 10px;
          background-color: #ffd75e;
        }
        .auth-modal-title {
          font-size: 22px;
          line-height: 1.25;
        }
        @media (min-width: 576px) {
          .auth-modal-title {
            font-size: 23px;
          }
        }
        .auth-modal-close {
          flex: none;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: rgba(255, 255, 255, 0.12);
          color: #fff;
          border: none;
          transition: background-color 0.15s ease;
        }
        .auth-modal-close:hover,
        .auth-modal-close:focus-visible {
          background-color: rgba(255, 255, 255, 0.22);
          color: #fff;
        }
        .auth-choice-card {
          display: flex;
          align-items: center;
          gap: 16px;
          text-align: left;
          padding: 18px 20px;
          border: 1px solid #e6ebf3;
          border-radius: 10px;
          background-color: #fff;
          transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
          cursor: pointer;
        }
        .auth-choice-card:hover,
        .auth-choice-card:focus-visible {
          border-color: #012f86;
          box-shadow: 0 4px 14px 0 rgba(1, 47, 134, 0.12);
          transform: translateY(-1px);
          outline: none;
        }
        .auth-choice-icon {
          flex: none;
          width: 44px;
          height: 44px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #f8faff;
          color: #012f86;
          font-size: 18px;
        }
        .auth-choice-icon--accent {
          background-color: #fff8e5;
          color: #a9791a;
        }
        .auth-modal-alert {
          background-color: #fdecec;
          border: 1px solid #f5c2c2;
          color: #8a2626;
          border-radius: 8px;
          padding: 12px 16px;
          display: flex;
          align-items: flex-start;
          gap: 10px;
          font-size: 14px;
        }
        .auth-submit-btn {
          transition: filter 0.15s ease;
        }
        .auth-submit-btn:hover:not(:disabled) {
          filter: brightness(1.1);
        }
      `}</style>
      <Modal
        show={isAuthModalOpen}
        onHide={handleClose}
        centered
        backdropClassName="auth-modal-backdrop"
        size="md"
        contentClassName="overflow-hidden"
      >
        <div className="auth-modal-header">
          <div className="auth-modal-header-text">
            <Modal.Title className="auth-modal-title wr-donation-header4 text-white text-uppercase mb-1">
              {mode === AUTH_MODES.CHOICE ? 'How would you like to continue?' : AUTH_MODE_META[mode].title}
            </Modal.Title>
            <p className="text-white mb-0 wr-donation-body4" style={{ opacity: 0.85 }}>
              {mode === AUTH_MODES.CHOICE
                ? 'Choose how you\'d like to complete your donation.'
                : AUTH_MODE_META[mode].subtitle}
            </p>
          </div>
          <button type="button" className="auth-modal-close" onClick={handleClose} aria-label="Close">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <Modal.Body className="p-4">
          {mode !== AUTH_MODES.CHOICE && (
            <Button
              variant="link"
              className="p-0 mb-3 text-decoration-none wr-donation-text-primary d-inline-flex align-items-center gap-1"
              onClick={() => { setMode(AUTH_MODES.CHOICE); setError(null); }}
            >
              <FontAwesomeIcon icon={faChevronLeft} size="xs" /> Back
            </Button>
          )}

          {error && (
            <div className="auth-modal-alert mb-3">
              <FontAwesomeIcon icon={faExclamationCircle} className="mt-1" />
              <div className="flex-grow-1">{error}</div>
              <button
                type="button"
                className="btn-close"
                style={{ fontSize: 11 }}
                aria-label="Dismiss"
                onClick={() => setError(null)}
              />
            </div>
          )}

          {mode === AUTH_MODES.CHOICE && (
            <div className="d-flex flex-column gap-3">
              <div className="auth-choice-card" role="button" tabIndex={0} onClick={() => setMode(AUTH_MODES.LOGIN)}>
                <span className="auth-choice-icon"><FontAwesomeIcon icon={faSignInAlt} /></span>
                <div>
                  <div className="wr-donation-subtitle1 mb-1">Login</div>
                  <p className="text-muted mb-0 small">Already have an account? Sign in to speed up checkout.</p>
                </div>
              </div>
              <div className="auth-choice-card" role="button" tabIndex={0} onClick={() => setMode(AUTH_MODES.SIGNUP)}>
                <span className="auth-choice-icon"><FontAwesomeIcon icon={faUserPlus} /></span>
                <div>
                  <div className="wr-donation-subtitle1 mb-1">Create Account</div>
                  <p className="text-muted mb-0 small">Save your details for future donations.</p>
                </div>
              </div>
              {!isHeaderMode && (
                <div className="auth-choice-card" role="button" tabIndex={0} onClick={handleGuest}>
                  <span className="auth-choice-icon auth-choice-icon--accent"><FontAwesomeIcon icon={faHandHoldingHeart} /></span>
                  <div>
                    <div className="wr-donation-subtitle1 mb-1">Continue as Guest</div>
                    <p className="text-muted mb-0 small">Donate without creating an account.</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {mode === AUTH_MODES.LOGIN && (
            <Form onSubmit={handleLogin}>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  placeholder="Enter your email"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Password</Form.Label>
                <PasswordInput
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter your password"
                />
              </Form.Group>
              <Button
                type="submit"
                className="w-100 bg-wr-donation-primary border-0 py-2 auth-submit-btn"
                disabled={isLoading}
              >
                {isLoading ? <Spinner size="sm" /> : 'Login & Continue'}
              </Button>
              <div className="text-center mt-3">
                <Button
                  variant="link"
                  className="p-0 text-muted"
                  onClick={() => { setMode(AUTH_MODES.RESET); setError(null); }}
                >
                  Forgot your password?
                </Button>
              </div>
            </Form>
          )}

          {mode === AUTH_MODES.RESET && (
            <Form onSubmit={async (e) => {
              e.preventDefault();
              setIsLoading(true);
              setError(null);
              try {
                const { error } = await supabase.auth.resetPasswordForEmail(email, {
                  redirectTo: `${window.location.origin}/reset-password`
                });
                if (error) throw new Error(error.message);
                toast.success('Password reset link sent! Check your inbox.');
                setMode(AUTH_MODES.LOGIN);
              } catch (err) {
                setError(err.message);
              } finally {
                setIsLoading(false);
              }
            }}>
              <p className="text-muted mb-3">
                Enter your email and we'll send you a link to reset your password.
              </p>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  placeholder="Enter your email"
                />
              </Form.Group>
              <Button
                type="submit"
                className="w-100 bg-wr-donation-primary border-0 py-2 auth-submit-btn"
                disabled={isLoading}
              >
                {isLoading ? <Spinner size="sm" /> : 'Send Reset Link'}
              </Button>
              <div className="text-center mt-3">
                <Button
                  variant="link"
                  className="p-0 text-muted"
                  onClick={() => { setMode(AUTH_MODES.LOGIN); setError(null); }}
                >
                  Back to login
                </Button>
              </div>
            </Form>
          )}

          {mode === AUTH_MODES.SIGNUP && (
            <Form onSubmit={handleSignup}>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  placeholder="Enter your email"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Password</Form.Label>
                <PasswordInput
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Create a password"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Confirm Password</Form.Label>
                <PasswordInput
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                />
              </Form.Group>
              <Button
                type="submit"
                className="w-100 bg-wr-donation-primary border-0 py-2 auth-submit-btn"
                disabled={isLoading}
              >
                {isLoading ? <Spinner size="sm" /> : 'Create Account & Continue'}
              </Button>
            </Form>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}

export { AuthChoiceModal };
